import { Controller } from '@hotwired/stimulus'

window.emailFirstResetRecaptchaPassed = () => {
  document.querySelectorAll('[data-email-first-reset-submit]').forEach((button) => {
    button.disabled = false
  })
}

window.emailFirstResetRecaptchaExpired = () => {
  document.querySelectorAll('[data-email-first-reset-submit]').forEach((button) => {
    button.disabled = true
  })
}

export default class extends Controller {
  static targets = ['step', 'email', 'error', 'continue', 'captured', 'campus', 'passwordPanel', 'createPanel',
    'securityPanel', 'recaptcha', 'securityWait', 'verified', 'campusespGoogle']

  static values = { domains: String, recaptcha: Boolean }

  connect() {
    if (window.location.hash === '#forgot-password') this.showStep('forgot', { animate: false })
    this.revealCampusespGoogle()
  }

  lookup(event) {
    event.preventDefault()
    const email = this.emailTarget.value.trim()
    if (!this.emailValid(email)) {
      this.showError('Enter a valid email, like name@example.com.')
      return
    }

    this.clearError()
    if (this.domainCovered(email) && this.hasCampusTarget) this.showStep('campus')
    this.setChecking(true)
    this.submitEmail(event.target.action, email)
  }

  emailValid(email) {
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)
  }

  domainCovered(email) {
    const domain = email.split('@').pop().toLowerCase()
    return this.domainList().some((configured) => domain === configured || domain.endsWith(`.${configured}`))
  }

  domainList() {
    return this.domainsValue
      .split(',')
      .map((domain) => domain.trim().toLowerCase())
      .filter(Boolean)
  }

  async submitEmail(url, email, { escape = false } = {}) {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    const body = escape ? { email, escape: true } : { email }
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
        },
        body: JSON.stringify(body),
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        this.showStep('email')
        this.showError(payload.error || 'Enter a valid email, like name@example.com.')
        return
      }
      this.showStep(payload.step)
      this.setChecking(false)
    } catch {
      this.showStep('email')
      this.showError('Could not check that email. Try again.')
    }
  }

  signIn(event) {
    this.probeBeforeSubmit(event, 'X-Account-Sign-In')
  }

  signUp(event) {
    this.probeBeforeSubmit(event, 'X-Account-Sign-Up')
  }

  closeSecurity(event) {
    event.preventDefault()
    this.allowNativeSignIn = false
    if (this.securityStep) {
      this.showFormPanel(this.securityStep)
      return
    }

    this.showPasswordPanel()
  }

  showStep(step, { animate = true } = {}) {
    if (step === 'password') this.showPasswordPanel()
    if (step === 'create') this.showCreatePanel()
    this.copyEmail()
    this.revealCampusespGoogle()
    window.clearTimeout(this.stepTimer)
    const next = this.stepTargets.find((element) => element.dataset.step === step)
    const current = this.stepTargets.find((element) => !element.hidden && element !== next)
    if (!next || !current || !animate || this.reduceMotion()) {
      this.revealStep(next)
      return
    }

    window.clearTimeout(this.stepTimer)
    current.classList.add('spotlight-step--leave')
    this.stepTimer = window.setTimeout(() => {
      current.classList.remove('spotlight-step--leave')
      this.revealStep(next)
      next.classList.add('spotlight-step--enter')
      window.setTimeout(() => next.classList.remove('spotlight-step--enter'), 180)
    }, 180)
  }

  revealStep(next) {
    const changed = Boolean(next?.hidden)
    this.stepTargets.forEach((element) => {
      element.hidden = element !== next
      element.classList.remove('spotlight-step--leave', 'spotlight-step--enter')
    })
    if (changed) this.focusHeading(next)
  }

  // "Welcome back" was receiving this focus, and the theme's focus ring
  // boxed those words. The caret belongs in the password field. A locked
  // account has no field, so leave focus where it is. Create account asks
  // for a name before a password, so the caret starts on the first name.
  focusHeading(root) {
    if (!root) return

    const createStep = root.dataset.step === 'create' || root.dataset.emailFirstTarget === 'createPanel'
    if (createStep) {
      const name = root.querySelector('input[name="user[first_name]"]:not([disabled])')
      if (name) {
        name.focus()
        return
      }
    }

    const password = root.querySelector('input[type="password"]:not([disabled])')
    if (password) {
      password.focus()
      return
    }

    const passwordStep = root.dataset.step === 'password' || root.dataset.emailFirstTarget === 'passwordPanel'
    if (passwordStep) return

    const heading = root.querySelector('h1, h2')
    if (!heading) return

    heading.setAttribute('tabindex', '-1')
    heading.focus()
  }

  reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }

  startCampus(event) {
    const url = event.currentTarget.dataset.campusHandoff
    if (!url) return

    event.preventDefault()
    window.location.assign(url)
  }

  escapeToPassword(event) {
    event.preventDefault()
    const email = this.detectedEmail()
    if (!this.emailValid(email)) return

    this.emailTarget.value = email
    this.clearError()
    this.setChecking(true)
    this.submitEmail(this.emailTarget.form.action, email, { escape: true })
  }

  openForgot(event) {
    event.preventDefault()
    this.clearError()
    const current = this.stepTargets.find((element) => !element.hidden)
    this.forgotReturnStep = current?.dataset.step === 'forgot' ? 'email' : (current?.dataset.step || 'email')
    this.showStep('forgot')
  }

  closeForgot(event) {
    if (!this.hasStepTarget) return

    event.preventDefault()
    this.showStep(this.forgotReturnStep || 'email')
  }

  detectedEmail() {
    const detected = this.element.querySelector('[data-campus="detected"]')
    const chip = detected?.querySelector('.spotlight-chip [data-email-first-target="captured"]')
    const hidden = detected?.querySelector('input[name="email"]')
    return chip?.textContent?.trim() || hidden?.value?.trim() || this.emailTarget.value.trim()
  }

  changeEmail(event) {
    event.preventDefault()
    const chip = event.currentTarget.closest('.spotlight-chip')
    const value = chip?.querySelector('[data-email-first-target="captured"]')?.textContent?.trim()
    if (value) this.emailTarget.value = value
    this.clearError()
    this.showStep('email')
  }

  togglePassword(event) {
    const button = event.currentTarget
    const input = button.parentElement.querySelector('input')
    const revealed = input.type === 'text'
    input.type = revealed ? 'password' : 'text'
    const verb = revealed ? 'Show' : 'Hide'
    button.textContent = verb
    button.setAttribute('aria-pressed', revealed ? 'false' : 'true')
    const field = button.dataset.passwordLabel || 'password'
    button.setAttribute('aria-label', `${verb} ${field}`)
  }

  revealCampusespGoogle() {
    if (!this.hasCampusespGoogleTarget || !this.hasEmailTarget) return

    const show = /^[^@\s]+@campusesp\.com$/i.test(this.emailTarget.value.trim())
    this.campusespGoogleTargets.forEach((element) => {
      element.hidden = !show
    })
  }

  copyEmail() {
    const email = this.emailTarget.value.trim()
    if (!email) return

    this.capturedTargets.forEach((element) => {
      if (element instanceof HTMLInputElement) element.value = email
      else element.textContent = email
    })
  }

  setChecking(checking) {
    this.continueTarget.disabled = checking
    this.continueTarget.textContent = checking ? 'Checking…' : 'Continue'
  }

  showError(message) {
    this.errorTarget.hidden = false
    this.errorTarget.textContent = message
    this.emailTarget.setAttribute('aria-invalid', 'true')
    this.setChecking(false)
  }

  clearError() {
    this.errorTarget.hidden = true
    this.errorTarget.textContent = ''
    this.emailTarget.setAttribute('aria-invalid', 'false')
  }

  wafNeedsToken() {
    return window.AwsWafIntegration?.hasToken && !window.AwsWafIntegration.hasToken()
  }

  probeBeforeSubmit(event, headerName) {
    if (!this.recaptchaValue || this.allowNativeSignIn) return
    if (this.wafNeedsToken()) return

    event.preventDefault()
    this.probeForm(event.target, headerName)
  }

  async probeForm(form, headerName) {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': token,
          [headerName]: '1',
        },
        redirect: 'manual',
      })
      const payload = await response.json().catch(() => ({}))
      if (payload.step === 'security') {
        this.openSecurity(form)
        return
      }
      if (payload.step === 'mfa') {
        this.openMfa(payload)
        return
      }
      if (payload.redirect) {
        window.location.assign(payload.redirect)
        return
      }
    } catch {
      // A failed probe falls through to the normal post.
    }

    this.allowNativeSignIn = true
    form.requestSubmit()
  }

  openSecurity(form) {
    const step = form.closest('[data-step]')
    this.securityForm = form
    this.securityStep = step
    const formPanel = this.formPanel(step)
    const securityPanel = this.panelPart(step, 'securityPanel')
    if (formPanel) formPanel.hidden = true
    if (securityPanel) securityPanel.hidden = false
    this.setSecurityState(step, { waiting: true })
    this.renderRecaptcha(step)
    this.focusHeading(securityPanel)
  }

  showPassword(event) {
    event.preventDefault()
    this.showStep('password')
  }

  openMfa(payload) {
    const step = this.stepTargets.find((element) => element.dataset.step === 'mfa')
    if (!step) return

    const phone = step.querySelector('[data-login-code-target="phone"]')
    if (phone && payload.phone) phone.textContent = payload.phone
    this.showStep('mfa')
    window.setTimeout(() => {
      const code = this.application.getControllerForElementAndIdentifier(step, 'login-code')
      code?.start(payload.resend_in, { focus: true })
    }, 220)
  }

  showPasswordPanel() {
    this.allowNativeSignIn = false
    const step = this.hasPasswordPanelTarget ? this.passwordPanelTarget.closest('[data-step]') : null
    this.showFormPanel(step)
  }

  showCreatePanel() {
    this.allowNativeSignIn = false
    const step = this.hasCreatePanelTarget ? this.createPanelTarget.closest('[data-step]') : null
    this.showFormPanel(step)
  }

  showFormPanel(step) {
    const formPanel = this.formPanel(step)
    const securityPanel = this.panelPart(step, 'securityPanel')
    const returning = securityPanel && !securityPanel.hidden && step && !step.hidden
    if (formPanel) formPanel.hidden = false
    if (securityPanel) securityPanel.hidden = true
    if (returning) this.focusHeading(formPanel)
  }

  renderRecaptcha(step) {
    const mount = this.panelPart(step, 'recaptcha')
    if (!mount || mount.dataset.mounted) return

    const sitekey = mount.dataset.sitekey
    if (!sitekey) return
    if (window.grecaptcha?.render) {
      window.grecaptcha.ready(() => this.mountRecaptcha(mount, sitekey))
      return
    }

    this.pendingRecaptcha = { mount, sitekey }
    if (document.querySelector('script[data-spotlight-recaptcha]')) return

    window.spotlightMountRecaptcha = () => {
      const pending = this.pendingRecaptcha
      if (pending) this.mountRecaptcha(pending.mount, pending.sitekey)
    }
    const script = document.createElement('script')
    script.src = 'https://www.google.com/recaptcha/api.js?onload=spotlightMountRecaptcha&render=explicit'
    script.async = true
    script.dataset.spotlightRecaptcha = '1'
    document.head.appendChild(script)
  }

  mountRecaptcha(mount, sitekey) {
    if (!mount || mount.dataset.mounted || !window.grecaptcha?.render) return

    mount.dataset.mounted = '1'
    window.grecaptcha.render(mount, {
      sitekey,
      callback: (token) => this.finishSecurityCheck(token),
      'expired-callback': () => this.expireSecurityCheck(),
    })
  }

  finishSecurityCheck(token) {
    const form = this.securityForm || this.passwordForm()
    if (!form) return

    this.storeRecaptchaToken(form, token)
    this.setSecurityState(this.securityStep || form.closest('[data-step]'), { waiting: false })
    this.allowNativeSignIn = true
    form.requestSubmit()
  }

  storeRecaptchaToken(form, token) {
    let field = form.querySelector('textarea[name="g-recaptcha-response"]')
    if (!field) {
      field = document.createElement('textarea')
      field.name = 'g-recaptcha-response'
      field.hidden = true
      form.appendChild(field)
    }
    field.value = token
  }

  expireSecurityCheck() {
    this.setSecurityState(this.securityStep, { waiting: true })
    this.allowNativeSignIn = false
  }

  setSecurityState(step, { waiting }) {
    const wait = this.panelPart(step, 'securityWait')
    const verified = this.panelPart(step, 'verified')
    if (wait) wait.hidden = !waiting
    if (verified) verified.hidden = waiting
  }

  formPanel(step) {
    return step?.querySelector(
      '[data-email-first-target="passwordPanel"], [data-email-first-target="createPanel"]',
    )
  }

  panelPart(step, name) {
    return step?.querySelector(`[data-email-first-target="${name}"]`)
  }

  passwordForm() {
    return this.hasPasswordPanelTarget ? this.passwordPanelTarget.querySelector('form') : null
  }
}
